/**
 * Navigation Bar Component
 * Purpose: Main navigation with authentication controls
 * Author: Traklet App
 * Created: 2025
 */

import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircle,
  Dashboard,
  ExitToApp,
  Link as LinkIcon,
  AdminPanelSettings,
  Help,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMenuClose();
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const renderUserMenu = () => (
    <Menu
      anchorEl={anchorEl}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      keepMounted
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={() => handleNavigation('/dashboard')}>
        <Dashboard sx={{ mr: 1 }} />
        Dashboard
      </MenuItem>
      <MenuItem onClick={() => handleNavigation('/guide')}>
        <Help sx={{ mr: 1 }} />
        Guide
      </MenuItem>
      {user?.role === 'admin' && (
        <MenuItem onClick={() => handleNavigation('/admin')}>
          <AdminPanelSettings sx={{ mr: 1 }} />
          Admin Panel
        </MenuItem>
      )}
      <MenuItem onClick={handleLogout}>
        <ExitToApp sx={{ mr: 1 }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <AppBar position="static" elevation={1}>
      <Toolbar>
        {/* Logo and Brand */}
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <LinkIcon sx={{ mr: 1, fontSize: 28 }} />
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(45deg, #ffffff 30%, #e3f2fd 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Traklet
          </Typography>
        </Box>

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Navigation Items */}
        {isAuthenticated ? (
          <Box display="flex" alignItems="center" gap={1}>
            {!isMobile && (
              <>
                <Button
                  color="inherit"
                  onClick={() => navigate('/dashboard')}
                  sx={{
                    fontWeight: isActivePath('/dashboard') ? 600 : 400,
                    textDecoration: isActivePath('/dashboard') ? 'underline' : 'none',
                  }}
                >
                  Dashboard
                </Button>
                <Button
                  color="inherit"
                  onClick={() => navigate('/guide')}
                  sx={{
                    fontWeight: isActivePath('/guide') ? 600 : 400,
                    textDecoration: isActivePath('/guide') ? 'underline' : 'none',
                  }}
                >
                  Guide
                </Button>
              </>
            )}
            
            {/* User Profile Menu */}
            <Box display="flex" alignItems="center" gap={1}>
              {!isMobile && (
                <Typography variant="body2" color="inherit">
                  {user?.email}
                </Typography>
              )}
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="primary-search-account-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  <AccountCircle />
                </Avatar>
              </IconButton>
            </Box>
            {renderUserMenu()}
          </Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              color="inherit"
              onClick={() => navigate('/guide')}
              sx={{
                fontWeight: isActivePath('/guide') ? 600 : 400,
                textDecoration: isActivePath('/guide') ? 'underline' : 'none',
              }}
            >
              Guide
            </Button>
            <Button
              color="inherit"
              onClick={() => navigate('/login')}
              sx={{
                fontWeight: isActivePath('/login') ? 600 : 400,
                textDecoration: isActivePath('/login') ? 'underline' : 'none',
              }}
            >
              Login
            </Button>
            <Button
              variant="outlined"
              color="inherit"
              onClick={() => navigate('/register')}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
                fontWeight: isActivePath('/register') ? 600 : 400,
              }}
            >
              Sign Up
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;